import * as React from 'react';
import { Toast } from '../Toast/Toast';
import { ToastManagerActionKind } from './ToastManagerReducer';
import { dispatch } from './ToastManagerStore';
import { callMultiple } from '../../utils/callMultiple';
const timeouts = new Map();
const uniqueIdCache = {};
const createUniqueId = (prefix) => {
    if (!uniqueIdCache[prefix]) {
        uniqueIdCache[prefix] = 0;
    }
    return `${prefix}-${++uniqueIdCache[prefix]}`;
};
function propsWithoutMessage(props) {
    const { message, ...restProps } = props;
    return restProps;
}
function isToastElement(toast) {
    return React.isValidElement(toast);
}
export function createToastApi({ messageParser = (message) => {
    const errorMessage = `createToastApi: The default parser can't parse this message, provide a custom parser with the messageParser option.`;
    if (Array.isArray(message)) {
        return message;
    }
    if (typeof message !== 'string' && !React.isValidElement(message)) {
        throw new Error(errorMessage);
    }
    return message;
}, } = {}) {
    const toastApi = {
        add: (toast, { dismissable = true, persistent = false, delayIn = 0, delayOut = 5000, } = {}) => {
            const createToast = dismissable
                ? toastApi.createDismissable
                : toastApi.create;
            const consistentKey = createUniqueId('mollie-ui-toast');
            let payload = isToastElement(toast)
                ? React.cloneElement(toast, { key: consistentKey })
                : toast && typeof toast === 'object' && 'message' in toast
                    ? createToast(toast.message, propsWithoutMessage(toast), consistentKey)
                    : createToast(toast, undefined, consistentKey);
            setTimeout(() => {
                dispatch({
                    type: ToastManagerActionKind.ADD,
                    payload,
                });
            }, delayIn);
            if (!persistent) {
                timeouts.set(payload, setTimeout(() => {
                    dispatch({
                        type: ToastManagerActionKind.REMOVE,
                        payload,
                    });
                }, delayOut));
            }
            return {
                remove() {
                    dispatch({ type: ToastManagerActionKind.REMOVE, payload });
                },
                update(toast, { dismissable: dismissableUpdated = dismissable, persistent: persistentUpdated = persistent, delayOut: delayOutUpdated = delayOut, } = {}) {
                    const createToast = dismissableUpdated
                        ? toastApi.createDismissable
                        : toastApi.create;
                    const nextPayload = isToastElement(toast)
                        ? React.cloneElement(toast, { key: consistentKey })
                        : toast && typeof toast === 'object' && 'message' in toast
                            ? createToast(toast.message, propsWithoutMessage(toast), consistentKey)
                            : createToast(toast, undefined, consistentKey);
                    dispatch({
                        type: ToastManagerActionKind.UPDATE,
                        payload,
                        nextPayload,
                    });
                    timeouts.delete(payload);
                    payload = nextPayload;
                    if (!persistentUpdated) {
                        timeouts.set(payload, setTimeout(() => {
                            dispatch({
                                type: ToastManagerActionKind.REMOVE,
                                payload,
                            });
                        }, delayOutUpdated));
                    }
                    return this;
                },
            };
        },
        remove: (toast) => {
            clearTimeout(timeouts.get(toast));
            timeouts.delete(toast);
            dispatch({ type: ToastManagerActionKind.REMOVE, payload: toast });
        },
        clear: () => {
            timeouts.forEach((timeout, toast) => {
                clearTimeout(timeout);
                timeouts.delete(toast);
            });
            dispatch({ type: ToastManagerActionKind.CLEAR });
        },
        create: (message, props = {}, key = createUniqueId('mollie-ui-toast')) => {
            const { messageValues, ...restProps } = props;
            const parsedMessage = messageParser(message, messageValues);
            return (React.createElement(Toast, { ...restProps, key: key }, parsedMessage));
        },
        createDismissable: (message, props = {}, key = createUniqueId('mollie-ui-toast')) => {
            const { onClose, messageValues, ...restProps } = props;
            const parsedMessage = messageParser(message, messageValues);
            const internalOnClose = () => toastApi.remove(toast);
            const toast = (React.createElement(Toast, { ...restProps, dismissable: true, onClose: callMultiple(internalOnClose, onClose), key: key }, parsedMessage));
            return toast;
        },
        notify: (message, action) => {
            const consistentKey = createUniqueId('mollie-ui-toast');
            const props = typeof message === 'string' ? { message } : message;
            const { message: extractedMessage, onClose, messageValues, ...restProps } = props;
            const parsedMessage = messageParser(extractedMessage, messageValues);
            const internalOnClose = () => toastApi.remove(payload);
            const payload = (React.createElement(Toast, { ...restProps, elementType: "button", variant: "notification", onClick: callMultiple(internalOnClose, action), onClose: callMultiple(internalOnClose, onClose), key: consistentKey }, parsedMessage));
            dispatch({
                type: ToastManagerActionKind.ADD,
                payload,
            });
            return {
                remove() {
                    dispatch({ type: ToastManagerActionKind.REMOVE, payload });
                },
            };
        },
    };
    return toastApi;
}
export const toastApi = createToastApi();
//# sourceMappingURL=toastApi.js.map